// src/components/CreateAdmin.js
import React, { useState } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import api from '../utils/Api';

function CreateAdmin() {
  const [formData, setFormData] = useState({
    nombre: '',
    correoContacto: '',
    password: ''
  });
  const [mensaje, setMensaje] = useState('');
  const [tipoMensaje, setTipoMensaje] = useState('success'); // 'success' | 'error'
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData((s) => ({ ...s, [field]: value }));
    setMensaje('');
  };

  const validate = () => {
    const { nombre, correoContacto, password } = formData;
    if (!nombre?.trim() || !correoContacto?.trim() || !password) {
      return 'Todos los campos son obligatorios';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(correoContacto.trim())) {
      return 'El correo no tiene un formato válido';
    }
    if (password.length < 6) {
      return 'La contraseña debe tener al menos 6 caracteres';
    }
    return null;
  };

  const handleCreateAdmin = async () => {
    const err = validate();
    if (err) {
      setTipoMensaje('error');
      setMensaje(err);
      return;
    }

    // Payload explícito: mapeamos correoContacto -> correo y forzamos rol admin
    const payload = {
      nombre: formData.nombre.trim(),
      correo: formData.correoContacto.trim().toLowerCase(),
      password: formData.password,
      rol: 'admin'
    };

    // Debug local (no mostrar password en producción)
    console.debug('[CreateAdmin] payload:', { ...payload, password: '***' });

    setLoading(true);
    try {
      const res = await api.post('/auth/create-admin', payload);
      const data = res && res.data ? res.data : res;

      if (!data || data.success === false) {
        const msg = data?.mensaje || '❌ Error al crear admin';
        setTipoMensaje('error');
        setMensaje(msg);
        return;
      }

      setTipoMensaje('success');
      setMensaje(data.mensaje || '✅ Admin creado correctamente');
      setFormData({ nombre: '', correoContacto: '', password: '' });
    } catch (err) {
      console.error('[CreateAdmin] error:', err.response?.data || err.message);
      const msg = err?.response?.data?.mensaje || err?.message || '❌ Error al crear admin';
      setTipoMensaje('error');
      setMensaje(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleCreateAdmin();
    }
  };

  const handleReset = () => {
    setFormData({ nombre: '', correoContacto: '', password: '' });
    setMensaje('');
    setTipoMensaje('success');
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>👑 Crear Usuario Admin</Typography>

          <TextField
            label="Nombre"
            fullWidth
            margin="normal"
            value={formData.nombre}
            onChange={(e) => handleChange('nombre', e.target.value)}
            autoComplete="name"
            onKeyDown={handleKeyDown}
          />

          <TextField
            label="Correo"
            type="email"
            fullWidth
            margin="normal"
            value={formData.correoContacto}
            onChange={(e) => handleChange('correoContacto', e.target.value)}
            autoComplete="email"
            onKeyDown={handleKeyDown}
          />

          <TextField
            label="Contraseña"
            type="password"
            fullWidth
            margin="normal"
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            autoComplete="new-password"
            onKeyDown={handleKeyDown}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleCreateAdmin}
              disabled={loading}
            >
              {loading ? <><CircularProgress size={18} sx={{ mr: 1 }} />Creando...</> : 'Crear Admin'}
            </Button>

            <Button
              variant="outlined"
              color="inherit"
              onClick={handleReset}
              disabled={loading}
            >
              Limpiar
            </Button>
          </Box>

          {mensaje && (
            <Alert severity={tipoMensaje} sx={{ mt: 2 }}>
              {mensaje}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

export default CreateAdmin;